import Reveal from "@/components/base/Reveal";
import Eyebrow from "@/components/base/Eyebrow";
import { courses, courseRequirements } from "@/mocks/site";
import { buildWhatsAppLink } from "@/utils/links";

const benefits = [
  { icon: "ri-team-line", text: "Grupos reducidos con práctica real" },
  { icon: "ri-award-line", text: "Constancia con certificación" },
  { icon: "ri-community-line", text: "Comunidad profesional ALÉA" },
];

export default function AcademySection() {
  return (
    <section id="cursos" className="relative overflow-hidden bg-background-100">
      <div className="gradient-clinic pointer-events-none absolute inset-0 opacity-70" />
      <div className="pointer-events-none absolute -left-24 top-20 h-[360px] w-[360px] rounded-full glow-clinic blur-3xl" />
      <div className="pointer-events-none absolute -right-20 bottom-20 h-[340px] w-[340px] rounded-full glow-lilac blur-3xl" />
      <div className="relative mx-auto w-full max-w-[1400px] px-5 py-20 md:px-10 md:py-28">
        <Reveal className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <Eyebrow>Unidad 03 · ALÉA Academy</Eyebrow>
            <h2 className="mt-3 font-heading text-4xl font-bold leading-[1.05] text-foreground-950 md:text-5xl">
              Formación estética para crecer
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-foreground-600">
              Cursos presenciales con práctica real, grupos reducidos y constancia con certificación.
            </p>
          </div>
          <div className="flex items-center gap-3 rounded-[16px] border border-accent-300 bg-accent-50 px-5 py-4">
            <i className="ri-alarm-warning-line text-2xl text-accent-700" />
            <span className="text-sm leading-snug text-foreground-700">
              <strong className="font-semibold text-foreground-950">Cupos limitados</strong>
              <br />
              por generación
            </span>
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {courses.slice(0, 3).map((course, index) => (
            <Reveal
              key={course.title}
              delay={index * 100}
              className="card-soft group relative flex flex-col overflow-hidden rounded-[24px] border border-secondary-200 bg-background-50 p-6 transition-all duration-500 hover:-translate-y-1 hover:card-premium md:p-7"
            >
              <span className="absolute inset-x-0 top-0 h-[3px] bar-clinic" />
              <span className="inline-flex w-fit items-center gap-2 rounded-full bg-accent-100 px-3 py-1 font-label text-[10px] font-semibold uppercase tracking-[0.14em] text-accent-900">
                <i className="ri-fire-line text-sm" />
                {course.spots}
              </span>
              <h3 className="mt-5 font-heading text-xl font-bold leading-snug text-foreground-950">
                {course.title}
              </h3>
              <p className="mt-2.5 flex-1 text-sm leading-relaxed text-foreground-600">{course.text}</p>

              <dl className="mt-6 space-y-2.5 border-t border-secondary-200 pt-5 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="flex items-center gap-2 text-foreground-500">
                    <i className="ri-calendar-line text-base" />
                    Próxima fecha
                  </dt>
                  <dd className="text-foreground-900">{course.date}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="flex items-center gap-2 text-foreground-500">
                    <i className="ri-time-line text-base" />
                    Duración
                  </dt>
                  <dd className="text-foreground-900">{course.duration}</dd>
                </div>
              </dl>

              <a
                href={buildWhatsAppLink(`Hola ALÉA, quiero inscribirme al curso de ${course.title}.`)}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex min-h-[48px] items-center justify-center gap-2 whitespace-nowrap cta-primary rounded-[16px] bg-primary-500 px-5 font-label text-[11px] font-semibold uppercase tracking-[0.14em] text-background-50 transition-colors duration-300 hover:bg-primary-600"
              >
                Solicitar información
                <i className="ri-arrow-right-line" />
              </a>
            </Reveal>
          ))}
        </div>

        <Reveal delay={120} className="mt-6 grid grid-cols-1 gap-8 rounded-[24px] border border-secondary-200 bg-background-50 p-6 md:grid-cols-12 md:p-10">
          <div className="md:col-span-5">
            <h3 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              Registro, requisitos y anticipo
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              Aparta tu lugar con un anticipo del 30%. Te compartimos la lista completa de cursos y
              fechas actualizadas por WhatsApp.
            </p>

            <ul className="mt-6 grid grid-cols-1 gap-3">
              {benefits.map((benefit) => (
                <li key={benefit.text} className="flex items-center gap-3 text-sm text-foreground-700">
                  <i className={`${benefit.icon} text-lg text-accent-700`} />
                  {benefit.text}
                </li>
              ))}
            </ul>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <a
                href={buildWhatsAppLink("Hola ALÉA, quiero inscribirme al próximo curso de ALÉA Academy.")}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-[48px] items-center justify-center gap-2 whitespace-nowrap cta-primary rounded-[16px] bg-primary-500 px-5 font-label text-[11px] font-semibold uppercase tracking-[0.14em] text-background-50 transition-colors duration-300 hover:bg-primary-600"
              >
                Inscribirme al próximo curso
              </a>
            </div>
          </div>

          <ul className="grid grid-cols-1 gap-3 md:col-span-7 md:grid-cols-2 md:content-start">
            {courseRequirements.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 rounded-[16px] border border-secondary-200 bg-background-100 p-4 text-sm leading-relaxed text-foreground-700"
              >
                <i className="ri-checkbox-circle-line mt-0.5 text-lg text-accent-600" />
                {item}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}